import { APP_INITIALIZER, EnvironmentProviders, LOCALE_ID, Provider, importProvidersFrom } from '@angular/core';
import { FeatureFlagService, UiFeatureFlagModule } from '@celerofinancas/ui-feature-flag';
import { UiOAuthModule } from '@celerofinancas/ui-oauth';
import { registerLocaleData } from '@angular/common';
import localeBr from '@angular/common/locales/pt';
import { environment } from '@environments/environment';
import { CL_TOKEN_PROVIDERS } from './tokens';
import { OAuthConfiguration } from '@environments/oauth';
import { StoreModule } from '@shared/store.module';
import { INTERCEPTORS_LIST } from './interceptors';

/**
 * Preload feature flags. The demo has no Flagsmith.
 *
 * Without `environmentID`, `FlagsmithService.init()` rejects immediately (inside
 * the Promise executor, with no network round trip) — the `.catch()` makes sure
 * that rejection never stalls `APP_INITIALIZER`. But the SDK's own `init()` also
 * calls `startListening()` before validating the config, which would leave a
 * `setInterval` hitting `getFlags()` every 5 minutes forever (a network error
 * repeating in the console). That is why we do not even call `flag.init()` when no
 * `environmentID` is configured — it resolves right away, never starting the
 * polling.
 *
 * @param flag feature flag service
 * @returns promise
 */
export function preloadFeatureFlags(flag: FeatureFlagService): () => Promise<void> {
  return () => (environment.featureFlag?.environmentID ? flag.init().catch((): void => undefined) : Promise.resolve());
}

/**
 * Registers the pt-BR locale.
 *
 * Without it, Angular's `DatePipe` falls back to the default locale (`en-US`) and
 * the statement list shows weekdays in English — "Monday", "Wednesday". The
 * `weekDate` pipe from `ui-pipes` does **not** cover this: it only returns "Hoje",
 * "Ontem" and "Amanhã", and `null` for any other date, and in that case the
 * `financial-records-list` template falls back to
 * `{{ key | date: 'EEEE' | titlecase }}`.
 *
 * `serasa-cl-webcomponents` does not register it because it runs hosted inside
 * `celerov2-ui-client`, which does. This app is its own SPA, so it has to — the
 * same as `caixa-cl-webcomponents` does in `app.config.ts` and `element.module.ts`.
 * Here it lives in `SESSION_PROVIDERS`, shared by both bootstraps, so neither can
 * end up without it.
 */
registerLocaleData(localeBr, 'pt');

/**
 * Session providers shared by every application bootstrap (`app.config.ts` and
 * `element.module.ts`). Single source of truth — never duplicate this between the
 * bootstraps, or one drifts from the other silently (works when served, breaks as
 * a custom element).
 */
export const SESSION_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: preloadFeatureFlags,
    deps: [FeatureFlagService],
  },
  { provide: LOCALE_ID, useValue: 'pt' },
  ...INTERCEPTORS_LIST,
  ...CL_TOKEN_PROVIDERS,
  importProvidersFrom(
    StoreModule,
    UiOAuthModule.forRoot(environment, OAuthConfiguration),
    UiFeatureFlagModule.forRoot(environment?.featureFlag),
  ),
];
